import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef, useGridApiRef } from "@mui/x-data-grid";
import { CloseOutlined, LocalGasStationOutlined, SearchOutlined, UploadFileOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { VehicleGridType, VehicleLastFuelingType } from "../../../../types/types";
import findLastFuelingByVehicleResponse from "../../../../resources/Api/vehicle/findLastFuelingByVehicleResponse";
import { GRID_DEFAULT_LOCALE_TEXT } from "../../../../resources/location/utils/location";

type VehicleFuelingDialogProps = {
    open: boolean;
    onClose: () => void;
    token: string;
    customerId?: number;
    vehicles: Array<VehicleGridType>;
};

type FuelingRow = {
    id: number;
    name: string;
    vehiclePlate: string;
    fueling: VehicleLastFuelingType | null;
};

const formatNumber = (value: number | null | undefined, digits: number) =>
    value == null
        ? "-"
        : Number(value).toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const formatCurrency = (value: number | null | undefined, digits = 2) =>
    value == null ? "-" : `R$ ${formatNumber(value, digits)}`;

const renderHeader = (title: string) => () => (
    <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "Prometo", fontSize: "12px", color: "#fff" }}>
        {title}
    </Typography>
);

const textCell = (value?: string | null) => (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
        <Typography sx={{ fontFamily: "Prometo", fontSize: 13 }} noWrap title={value ?? undefined}>
            {value || "-"}
        </Typography>
    </Box>
);

const columns: GridColDef<FuelingRow>[] = [
    {
        field: "name",
        headerName: "Nome/Etiqueta",
        width: 170,
        renderHeader: renderHeader("Nome/Etiqueta"),
        renderCell: ({ row }) => textCell(row.name),
    },
    {
        field: "vehiclePlate",
        headerName: "Placa",
        width: 110,
        renderHeader: renderHeader("Placa"),
        renderCell: ({ row }) => textCell(row.vehiclePlate),
    },
    {
        field: "occurredAt",
        headerName: "Data/hora",
        type: "dateTime",
        width: 150,
        valueGetter: (_, row) => (row.fueling?.occurredAt ? dayjs(row.fueling.occurredAt).toDate() : null),
        valueFormatter: (value: Date | null) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"),
        renderHeader: renderHeader("Data/hora"),
    },
    {
        field: "liters",
        headerName: "Litros",
        type: "number",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (_, row) => row.fueling?.liters ?? null,
        valueFormatter: (value: number | null) => formatNumber(value, 3),
        renderHeader: renderHeader("Litros"),
    },
    {
        field: "totalValue",
        headerName: "Valor total",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueGetter: (_, row) => row.fueling?.totalValue ?? null,
        valueFormatter: (value: number | null) => formatCurrency(value),
        renderHeader: renderHeader("Valor total"),
    },
    {
        field: "unitPrice",
        headerName: "Valor unitário",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueGetter: (_, row) => row.fueling?.unitPrice ?? null,
        valueFormatter: (value: number | null) => formatCurrency(value, 3),
        renderHeader: renderHeader("Valor unitário"),
    },
    {
        field: "fuelType",
        headerName: "Combustível",
        width: 130,
        valueGetter: (_, row) => row.fueling?.fuelType ?? "",
        renderHeader: renderHeader("Combustível"),
        renderCell: ({ value }) => textCell(value),
    },
    {
        field: "odometer",
        headerName: "Odômetro",
        width: 110,
        valueGetter: (_, row) => row.fueling?.odometer ?? "",
        renderHeader: renderHeader("Odômetro"),
        renderCell: ({ value }) => textCell(value),
    },
    {
        field: "supplierName",
        headerName: "Posto",
        width: 230,
        valueGetter: (_, row) =>
            [row.fueling?.supplierName, row.fueling?.supplierCity].filter(Boolean).join(" - "),
        renderHeader: renderHeader("Posto"),
        renderCell: ({ value }) => textCell(value),
    },
    {
        field: "driverName",
        headerName: "Condutor",
        width: 160,
        valueGetter: (_, row) => row.fueling?.driverName ?? "",
        renderHeader: renderHeader("Condutor"),
        renderCell: ({ value }) => textCell(value),
    },
    {
        field: "source",
        headerName: "Origem",
        width: 190,
        valueGetter: (_, row) => row.fueling?.sourceDescription ?? "Sem registro",
        renderHeader: renderHeader("Origem"),
        renderCell: ({ row }) => (
            <Chip
                size="small"
                label={row.fueling?.sourceDescription ?? "Sem registro"}
                sx={{
                    fontFamily: "Prometo",
                    fontWeight: 700,
                    color: row.fueling ? "#fff" : "#5f6b6d",
                    bgcolor: !row.fueling ? "#e6e9ea" : row.fueling.source === "FUELING_MODULE" ? "#006C77" : "#8a5a00",
                }}
            />
        ),
    },
];

const VehicleFuelingDialog = ({ open, onClose, token, customerId, vehicles }: VehicleFuelingDialogProps) => {
    const [fuelings, setFuelings] = useState<Record<number, VehicleLastFuelingType>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const apiRef = useGridApiRef();

    useEffect(() => {
        if (!open || !customerId) return;

        let ignore = false;
        setLoading(true);
        setError("");
        setFuelings({});

        findLastFuelingByVehicleResponse(token, customerId, vehicles.map((vehicle) => vehicle.id))
            .then(({ data }) => {
                if (ignore) return;
                const byVehicle: Record<number, VehicleLastFuelingType> = {};
                (data as Array<VehicleLastFuelingType>).forEach((item) => { byVehicle[item.vehicleId] = item; });
                setFuelings(byVehicle);
            })
            .catch((err: any) => {
                if (!ignore) setError(err?.response?.data?.reason ?? "Não foi possível buscar os abastecimentos.");
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        return () => {
            ignore = true;
        };
        // a lista da grade e recarregada a cada minuto; so busca de novo quando o modal abre
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customerId, token]);

    const rows: Array<FuelingRow> = useMemo(() => {
        const q = search.trim().toLowerCase();
        return vehicles
            .map((vehicle) => ({
                id: vehicle.id,
                name: vehicle.name,
                vehiclePlate: vehicle.vehiclePlate,
                fueling: fuelings[vehicle.id] ?? null,
            }))
            .filter((row) =>
                !q ||
                row.name?.toLowerCase().includes(q) ||
                row.vehiclePlate?.toLowerCase().includes(q) ||
                row.fueling?.supplierName?.toLowerCase().includes(q) ||
                row.fueling?.driverName?.toLowerCase().includes(q) ||
                row.fueling?.fuelType?.toLowerCase().includes(q)
            );
    }, [vehicles, fuelings, search]);

    const withDataCount = useMemo(() => rows.filter((row) => row.fueling).length, [rows]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            aria-labelledby="vehicle-fueling-dialog-title"
            PaperProps={{
                className: "!tw-overflow-hidden !tw-rounded-2xl tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth",
            }}
            sx={{
                "& .MuiDialog-paper": {
                    width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
                    maxHeight: "calc(100dvh - 24px)",
                    margin: { xs: 1.5, sm: 4 },
                    overflow: "hidden",
                    borderRadius: 4,
                    border: "1px solid rgba(0, 63, 74, 0.10)",
                    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, backgroundColor: "#ffff" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        padding: { xs: 2, sm: 2.5 },
                        color: "#ffff",
                        backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: { xs: "100%", sm: 280 } }}>
                        <LocalGasStationOutlined />
                        <Typography
                            id="vehicle-fueling-dialog-title"
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", fontFamily: "Prometo", color: "#ffff" }}
                        >
                            Últimos abastecimentos ({withDataCount} de {rows.length} veículos com registro)
                        </Typography>
                    </Box>

                    <TextField
                        size="small"
                        placeholder="Pesquisar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            width: { xs: "100%", sm: 280 },
                            "& .MuiInputBase-root": {
                                height: "42px",
                                fontFamily: "Prometo",
                                fontSize: "13px",
                                backgroundColor: "#fff",
                                borderRadius: 2,
                            },
                            "& fieldset": { border: "none" },
                        }}
                        InputProps={{
                            startAdornment: <SearchOutlined sx={{ fontSize: 18, mr: 0.5, color: "#666" }} />,
                            endAdornment: search && (
                                <Tooltip title="Limpar pesquisa" arrow placement="top">
                                    <IconButton size="small" aria-label="Limpar pesquisa" onClick={() => setSearch("")}>
                                        <CloseOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ),
                        }}
                    />
                </Box>

                <Box sx={{ padding: { xs: 1.5, sm: 2.5 }, minHeight: 0, backgroundColor: "#ffff" }}>
                    {error && (
                        <Typography sx={{ fontFamily: "Prometo", color: "#d32f2f", mb: 1 }}>{error}</Typography>
                    )}
                    <Box sx={{ height: { xs: "55dvh", sm: 460 }, width: "100%", overflow: "hidden", borderRadius: 3, border: "1px solid rgba(0, 63, 74, 0.12)" }}>
                        <DataGrid
                            apiRef={apiRef}
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            localeText={GRID_DEFAULT_LOCALE_TEXT}
                            density="compact"
                            disableRowSelectionOnClick
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                                sorting: { sortModel: [{ field: "occurredAt", sort: "desc" }] },
                            }}
                        />
                    </Box>

                    <DialogActions sx={{ px: 0, pb: 0, pt: 2, gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            startIcon={<UploadFileOutlined />}
                            onClick={() =>
                                apiRef.current.exportDataAsCsv({
                                    fileName: "ultimos-abastecimentos",
                                    delimiter: ";",
                                })
                            }
                            sx={{
                                minWidth: 120,
                                minHeight: 42,
                                borderRadius: 2,
                                bgcolor: "#00B700",
                                color: "#fff",
                                fontFamily: "Prometo",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#00B700", boxShadow: "0 8px 18px rgba(0, 183, 0, 0.22)" },
                            }}
                        >
                            Exportar
                        </Button>
                        <Button
                            onClick={onClose}
                            variant="contained"
                            startIcon={<CloseOutlined />}
                            sx={{
                                minWidth: 120,
                                minHeight: 42,
                                borderRadius: 2,
                                bgcolor: "#dc3545",
                                color: "#fff",
                                fontFamily: "Prometo",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#dc3545", boxShadow: "0 8px 18px rgba(220, 53, 69, 0.22)" },
                            }}
                        >
                            Fechar
                        </Button>
                    </DialogActions>
                </Box>
            </Box>
        </Dialog>
    );
};

export default VehicleFuelingDialog;
